<script setup lang="ts">
import { computed } from 'vue';
import { InputGroup, Typography, Link } from '@apparatix/library/core';
import DetailEditorBase from './DetailEditorBase.vue';
import type { ContactSchema, ContactErrors } from '@apparatix/schemas/Contact';
import { useContactStore } from '@apparatix/stores/useContactStore';
import { useActivities, useStringFunctions } from '@apparatix/composables';

type Props = {
  contactDetails: ContactSchema;
  noHeadingLabels?: boolean;
  errors?: ContactErrors;
}

const props = defineProps<Props>();
const store = useContactStore();
const { formattedUrl } = useStringFunctions();
const { postActivity } = useActivities();

const leftColLabel = computed(() => {
  if (props.noHeadingLabels) return undefined;
  return 'General';
});

const rightColLabel = computed(() => 'Get in Touch');

const addressUrl = computed(() => 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(props.contactDetails.addresses[0]?.fullAddress));

const getFullName = () => props.contactDetails?.fullName ?? [props.contactDetails?.firstName, props.contactDetails?.lastName].filter(Boolean).join(' ').trim();

</script>
<template>
  <DetailEditorBase :left-col-header="leftColLabel" :right-col-header="rightColLabel">
    <template #left-col>
      <InputGroup>
        <Typography variant="bold-label">
          Owner
        </Typography>
        <Typography variant="form-label">
          {{ props.contactDetails.owner.fullName }}
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Salutation
        </Typography>
        <Typography v-show="Boolean(props.contactDetails.salutation)" variant="form-label">
          {{ props.contactDetails.salutation }}
        </Typography>
        <Typography v-show="!Boolean(props.contactDetails.salutation)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          First Name
        </Typography>
        <Typography v-show="Boolean(props.contactDetails.firstName)" variant="form-label">
          {{ props.contactDetails.firstName }}
        </Typography>
        <Typography v-show="!Boolean(props.contactDetails.firstName)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Middle Name
        </Typography>
        <Typography v-show="Boolean(props.contactDetails.middleName)" variant="form-label">
          {{ props.contactDetails.middleName }}
        </Typography>
        <Typography v-show="!Boolean(props.contactDetails.middleName)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Last Name
        </Typography>
        <Typography v-show="Boolean(props.contactDetails.lastName)" variant="form-label">
          {{ props.contactDetails.lastName }}
        </Typography>
        <Typography v-show="!Boolean(props.contactDetails.lastName)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Suffix
        </Typography>
        <Typography v-show="Boolean(props.contactDetails.suffix)" variant="form-label">
          {{ props.contactDetails.suffix }}
        </Typography>
        <Typography v-show="!Boolean(props.contactDetails.suffix)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Nickname
        </Typography>
        <Typography v-show="!Boolean(props.contactDetails.nickname)" variant="form-label">
          {{ props.contactDetails.nickname }}
        </Typography>
        <Typography v-show="!Boolean(props.contactDetails.nickname)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Job Title
        </Typography>
        <Typography v-show="Boolean(props.contactDetails.jobTitle)" variant="form-label">
          {{ props.contactDetails.jobTitle }}
        </Typography>
        <Typography v-show="!Boolean(props.contactDetails.jobTitle)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Company Name
        </Typography>
        <Typography v-show="Boolean(props.contactDetails.companyName)" variant="form-label">
          {{ props.contactDetails.companyName }}
        </Typography>
        <Typography v-show="!Boolean(props.contactDetails.companyName)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
    </template>
    <template v-if="true" #right-col>
      <InputGroup>
        <Typography variant="bold-label">
          Emails
        </Typography>
        <div v-show="Boolean(props.contactDetails.emails.length !== 0)">
          <template v-for="email in props.contactDetails.emails" :key="email.id">
            <div class="flex items-center space-x-2">
              <Link :href="`mailto:${email.email}`" target="_blank" @click="postActivity(1, props.contactDetails.id, getFullName(), 'contacts')">
                {{ email.email }}
              </Link>
            </div>
          </template>
        </div>
        <Typography v-show="Boolean(props.contactDetails.emails.length === 0)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Phone Numbers
        </Typography>
        <div v-show="Boolean(props.contactDetails.phoneNumbers.length !== 0)">
          <template v-for="phoneNumber in props.contactDetails.phoneNumbers" :key="phoneNumber.id">
            <div class="flex items-center space-x-2">
              <Link :href="`tel:${phoneNumber.number}`" @pointerdown="postActivity(2, props.contactDetails.id, getFullName(), 'contacts')">
                {{ phoneNumber.number }}
              </Link>
              <Typography variant="form-label">
                ({{ phoneNumber.addressType }})
              </Typography>
            </div>
          </template>
        </div>
        <Typography v-show="Boolean(props.contactDetails.phoneNumbers.length === 0)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Websites
        </Typography>
        <div v-show="Boolean(props.contactDetails.webPages.length !== 0)">
          <template v-for="webPage in props.contactDetails.webPages" :key="webPage.id">
            <div class="flex items-center space-x-2">
              <Link :href="formattedUrl(webPage.url)" target="_blank" rel="noopener noreferrer">
                {{ webPage.url }}
              </Link>
              <Typography variant="form-label">
                ({{ webPage.addressType }})
              </Typography>
            </div>
          </template>
        </div>
        <Typography v-show="Boolean(props.contactDetails.webPages.length === 0)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <div class="flex items-center w-full pb-3 lg:pb-4 border-b border-[var(--p-content-border-color)]">
        <Typography variant="form-heading">
          Location
        </Typography>
      </div>
      <InputGroup>
        <Typography variant="bold-label">
          Address
        </Typography>
        <!--@TODO: We should modify the component to allow address formatting-->
        <Link
          v-show="Boolean(store.contactDetails.addresses[0]?.fullAddress)"
          :href="addressUrl"
          class="whitespace-pre-line"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ store.contactDetails.addresses[0]?.fullAddress ?? '' }}
        </Link>
        <Typography v-show="!Boolean(store.contactDetails.addresses[0]?.fullAddress)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
    </template>
  </DetailEditorBase>
</template>
